import clsx from 'clsx';
import { useLogo } from '../../helpers/hooks/LogoContext';
import styles from '../Login/Login.module.css';
import { AuthPageShell } from '../Login/AuthPageShell';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendForgotPasswordEmail } from '../../auth/_requests';
import { showToast } from '../../helpers/utils/showToast';

const initialValues = {
  email: '',
};
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required'),
});
const ForgotPasswordLogin = () => {
  const { logoUrl } = useLogo();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const response = await sendForgotPasswordEmail('TENANT_URL', values.email);
        if (
          response.message === 'user_not_found' ||
          response.message === 'user_type_not_authorized'
        ) {
          showToast({
            message: 'Email is not registered with us, please check again.',
            type: 'error',
          });
        } else {
          showToast({
            message: 'A reset link has been sent to your email. Please check your inbox.',
            type: 'success',
          });
          formik.setStatus('');
        }
      } catch (error) {
        console.error('Error sending reset link:', error);
        showToast({
          message: 'An error occurred while sending the reset link. Please try again later.',
          type: 'error',
        });
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });
  return (
    <AuthPageShell variant='center'>
      <div className={styles.loginCardShell}>
        <motion.main className={styles.main} initial={false}>
          <div className={styles.logoWrap}>
            <img src={logoUrl} className={styles.logo} alt='logo' decoding='async' />
          </div>

          <div className={styles.formTitle}>Reset your password.</div>
          <p className={styles.forgotText}>
            Please enter your registered email address to reset your password.
          </p>

          <form className={styles.form} onSubmit={formik.handleSubmit} noValidate>
            <div className={styles.formUpper}>
              <div className={styles.formBoxes}>
                <div className={styles.formBoxesInner}>
                  <div
                    className={'inputWrapper'}
                    title='Enter your registered email to reset your password'
                  >
                    <div className={'label'}>Email</div>

                    <div
                      className={clsx(
                        formik.touched.email && formik.errors.email ? styles.isInvalid : '',
                        formik.touched.email && !formik.errors.email ? styles.valid : '',
                        'inputField'
                      )}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          formik.handleSubmit();
                        }
                      }}
                    >
                      <input
                        {...formik.getFieldProps('email')}
                        placeholder='you@email.com'
                        type='email'
                        name='email'
                        autoComplete='email'
                      />
                    </div>
                    {formik.touched.email && formik.errors.email && (
                      <div className={styles.emailError}>
                        <span>{formik.errors.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <button
              type='submit'
              disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
              className={`${styles.submitBtn} ${
                (formik.isSubmitting || !formik.isValid || !formik.dirty) && styles.disableBtn
              }`}
            >
              {!loading && <span className='indicator-label'>Reset password</span>}
              {loading && (
                <span className={styles.indicatorProgress} style={{ display: 'block' }}>
                  Please wait...
                  <span className={styles.spinner}></span>
                </span>
              )}
            </button>
          </form>
          <div className={styles.signInBox}>
            <span>Got your password?</span>{' '}
            <button
              type='button'
              onClick={() => {
                navigate('/login');
              }}
              className={styles.forgotPassword}
            >
              Sign in
            </button>
          </div>
        </motion.main>
      </div>
    </AuthPageShell>
  );
};
export default ForgotPasswordLogin;
